import { type CSSProperties } from "react";
import type { EntityTypeDefinition, FieldDefinition } from "../../../lib/types";
import type { FkRule } from "../../hooks/use-wrapped-apis";

/**
 * Sprint 16c — FK-resolution rule editor. Each rule reads a wrapped response
 * field, finds the target entity whose match-field equals it, and sets a
 * reference field on the materialised row. This is what links wrapped data to
 * native Kuvio entities (the moat's "FK'd to what you already track").
 */
export function FkResolutionEditor({
  rules,
  onChange,
  targetFields,
  entityTypes,
}: {
  rules: FkRule[];
  onChange: (next: FkRule[]) => void;
  /** Fields on the entity type being materialised (the fk_field lives here). */
  targetFields: FieldDefinition[];
  entityTypes: EntityTypeDefinition[];
}) {
  function setRule(i: number, patch: Partial<FkRule>) {
    onChange(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRule() {
    onChange([...rules, { wrapped_field: "", target_entity_type: "", matches_entity_field: "", fk_field: "" }]);
  }
  function removeRule(i: number) {
    onChange(rules.filter((_, idx) => idx !== i));
  }

  // Reference-typed fields are the natural FK targets; fall back to all fields.
  const refFields = targetFields.filter((f) => f.type === "reference" || f.type === "references");
  const fkFieldChoices = refFields.length > 0 ? refFields : targetFields;

  return (
    <div>
      {rules.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 6 }}>
          No FK rules. Add one to link materialised rows to existing Contacts, Accounts, etc.
        </div>
      )}
      {rules.map((rule, i) => {
        const targetType = entityTypes.find((t) => t.slug === rule.target_entity_type);
        return (
          <div key={i} style={ruleBox}>
            <div style={ruleGrid}>
              <Labelled label="Response field">
                <input
                  value={rule.wrapped_field}
                  placeholder="requester.email"
                  onChange={(e) => setRule(i, { wrapped_field: e.target.value })}
                  style={{ ...input, fontFamily: "var(--font-mono)" }}
                />
              </Labelled>
              <Labelled label="Matches type">
                <select
                  value={rule.target_entity_type}
                  onChange={(e) => setRule(i, { target_entity_type: e.target.value, matches_entity_field: "" })}
                  style={input}
                >
                  <option value="">— type —</option>
                  {entityTypes.map((t) => (
                    <option key={t.slug} value={t.slug}>{t.name}</option>
                  ))}
                </select>
              </Labelled>
              <Labelled label="On field">
                <select
                  value={rule.matches_entity_field}
                  onChange={(e) => setRule(i, { matches_entity_field: e.target.value })}
                  style={input}
                  disabled={!targetType}
                >
                  <option value="">— field —</option>
                  {(targetType?.fields ?? []).map((f) => (
                    <option key={f.name} value={f.name}>{f.name}</option>
                  ))}
                </select>
              </Labelled>
              <Labelled label="Sets reference">
                <select
                  value={rule.fk_field}
                  onChange={(e) => setRule(i, { fk_field: e.target.value })}
                  style={input}
                >
                  <option value="">— field —</option>
                  {fkFieldChoices.map((f) => (
                    <option key={f.name} value={f.name}>{f.name}</option>
                  ))}
                </select>
              </Labelled>
            </div>
            <button type="button" onClick={() => removeRule(i)} style={removeBtn} aria-label="Remove rule">
              <i className="ti ti-x" style={{ fontSize: 13 }} aria-hidden="true" />
            </button>
          </div>
        );
      })}
      <button type="button" onClick={addRule} style={addBtn}>
        <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" /> Add FK rule
      </button>
    </div>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      <span style={{ fontSize: 10, fontWeight: 500, color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
      {children}
    </label>
  );
}

const ruleBox: CSSProperties = {
  display: "flex",
  alignItems: "flex-end",
  gap: 6,
  padding: "8px",
  marginBottom: 6,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-secondary)",
};
const ruleGrid: CSSProperties = {
  flex: 1,
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 6,
  minWidth: 0,
};
const input: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "6px 8px",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
};
const removeBtn: CSSProperties = {
  width: 26,
  height: 30,
  flexShrink: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
const addBtn: CSSProperties = {
  fontSize: 12,
  padding: "5px 10px",
  marginTop: 4,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
